<template>
  <Hero :title="data?.header.title" :subtitle="data?.header.subtitle" :narrow="true" :images="data?.header.images"/>
  <main class="main maxw--waist block">
    <form @submit.prevent="submit" class="form">
      <fieldset class="form__fieldset">
        <div class="form__row">
          <FormTextField class="field--w1-2" name="firstname" label="Voornaam" v-model="v$.firstname.$model" :validator="v$.firstname"/>
          <FormTextField class="field--w1-2" name="lastname" label="Achternaam" v-model="v$.lastname.$model" :validator="v$.lastname"/>
        </div>
        <div class="form__row">
          <FormTextField name="email" type="email" label="Email" v-model="v$.email.$model" :validator="v$.email"/>
        </div>
        <div class="form__row" v-if="!isDisabled">
          <NuxtTurnstile v-model="token" class="mt-2 mx-auto"/>
        </div>
        <div class="form__row">
          <div class="field">
            <button class="btn btn--full mt-2" type="submit" :disabled="isLoading">
              Verzenden
              <Spinner v-if="isLoading" />
            </button>
          </div>
        </div>
        <div class="form__row" v-if="!neutral">
          <span class="mt-1 field__status text--center" :class="feedbackClass">{{ feedbackMessage }}</span>
        </div>
      </fieldset>
    </form>
  </main>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';
import RegistrationPage from '~/typings/pages/registration_page';
import { default as Registration, RegistrationSubmission } from '@/typings/items/registration';

const { findOne, create } = useStrapi();

const { isLoading, startLoading, stopLoading } = useLoading();
const { token, isDisabled, verify } = useTurnstile();
const { feedbackMessage, resetStatus, setStatus, handleSuccess, neutral, feedbackClass } = useSubmitFeedback(
  'Registratie verzonden',
  'Er is iets fout gegaan. Probeer het later opnieuw'
);

useHead({
  title: 'Registreren'
});

const { data } = await useAsyncData(
  'registrationpage',
  () => findOne<RegistrationPage>('registration-page', { populate: ['header', 'header.images'] }),
  {
    transform: flattenStrapiObject
  }
);

const baseFormData = {
  firstname: '',
  lastname: '',
  email: ''
}

const formData = reactive<RegistrationSubmission>({ ...baseFormData });

async function submit(e: Event) {
  resetStatus();

  if (!(await v$.value.$validate()) || isLoading.value) return;

  startLoading();

  try {
    if (!(await verify())) {
      // Verification with turnstile failed
      setStatus(false);
      return;
    }

    await create<Registration>('registrations', formData);

    Object.assign(formData, baseFormData);
    v$.value.$reset();

    handleSuccess();
  } catch (e) {
    setStatus(false);
  } finally {
    stopLoading();
  }
}

const rules = computed(() => ({
  firstname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  lastname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  email: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    email: helpers.withMessage('Je dient een geldig emailadres in te vullen.', email)
  },
}));

const v$ = useVuelidate(rules, formData);
</script>
